import dayjs, {Dayjs} from 'dayjs';
import {getWeekRange} from '@utils/date';
import {
  ICheckinCycleDetail,
  ICurrentCheckinsInfoResponseData,
} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.interface';
import {CHECKLISTS_ENABLED} from '../checklist-availability';
import {generateObjectId} from '@utils/objectId';
import {isGoalsReflectionQuestion} from '../create-checkin-cycle/components/goals-content';
import {
  CheckinDeadlineInput,
  getCheckinDeadline,
} from '../shared/checkin-deadline.utils';

type CheckinQuestion = {
  _id?: string;
  question?: string;
  questionId?: string | null;
  response?: string | number | null;
  optional?: boolean;
  required?: boolean;
};

export type CheckinFormValues = {
  pulse?: {mood?: string | null; feedback?: string | null};
  questions?: {questions?: CheckinQuestion[]};
  priorities?: {
    currentCheckin?: Array<{text?: string; completed?: boolean}>;
  };
};

export type CheckinProgress = {
  answered: number;
  total: number;
  percentage: number;
  canSubmit: boolean;
};

export type CheckinDueMeta = {
  dueDate?: Dayjs;
  dueLabel?: string;
  dueDateLabel?: string;
  isOverdue: boolean;
};

export const markCheckinSubmitted = (
  checkins: ICurrentCheckinsInfoResponseData[],
  checkinId: string,
) =>
  checkins.map((checkin) =>
    checkin.id === checkinId ? {...checkin, isSubmitted: true} : checkin,
  );

export const getCycleId = (checkin?: ICurrentCheckinsInfoResponseData) => {
  if (!checkin) return undefined;
  if (typeof checkin.cycle === 'string') return checkin.cycle;
  return checkin.cycle?.id || checkin.cycleId;
};

const getOccurrenceTime = (checkin?: ICurrentCheckinsInfoResponseData) => {
  if (!checkin) return null;
  if (checkin.createdAt) {
    const createdAt = dayjs(checkin.createdAt);
    if (createdAt.isValid()) return createdAt.valueOf();
  }
  if (checkin.weekOfYear && checkin.year) {
    return dayjs(
      getWeekRange(checkin.weekOfYear, checkin.year).starts,
    ).valueOf();
  }
  return null;
};

const belongsToSameCycle = (
  first?: ICurrentCheckinsInfoResponseData,
  second?: ICurrentCheckinsInfoResponseData,
) => {
  const firstCycleId = getCycleId(first);
  const secondCycleId = getCycleId(second);
  return Boolean(
    firstCycleId && secondCycleId && firstCycleId === secondCycleId,
  );
};

export const getLatestCycleCheckin = (
  checkin: ICurrentCheckinsInfoResponseData | null | undefined,
  checkins: ICurrentCheckinsInfoResponseData[],
) => {
  if (!checkin) return null;
  const checkinTime = getOccurrenceTime(checkin);
  if (checkinTime === null) return checkin;

  return checkins.reduce<ICurrentCheckinsInfoResponseData>(
    (latest, candidate) => {
      if (!belongsToSameCycle(checkin, candidate)) return latest;
      const latestTime = getOccurrenceTime(latest);
      const candidateTime = getOccurrenceTime(candidate);
      return candidateTime !== null &&
        (latestTime === null || candidateTime > latestTime)
        ? candidate
        : latest;
    },
    checkin,
  );
};

export const isCheckinSuperseded = (
  checkin: ICurrentCheckinsInfoResponseData | null | undefined,
  checkins: ICurrentCheckinsInfoResponseData[],
) => {
  if (!checkin || checkin.isSubmitted) return false;
  const latest = getLatestCycleCheckin(checkin, checkins);
  return Boolean(latest?.id && checkin.id && latest.id !== checkin.id);
};

export const getActionablePendingCheckins = (
  checkins: ICurrentCheckinsInfoResponseData[],
) =>
  checkins.filter(
    (checkin) =>
      Boolean(checkin.id) &&
      !checkin.isSubmitted &&
      !checkin.isExcused &&
      !isCheckinSuperseded(checkin, checkins),
  );

export const hasAnswer = (response?: string | number | null) => {
  if (typeof response === 'number') return true;
  return typeof response === 'string' && response.trim().length > 0;
};

export const isQuestionRequired = (question?: CheckinQuestion) =>
  question?.required === true || question?.optional === false;

export const ensureQuestionResponseIds = <
  T extends {questionId?: string | null}
>(
  questions: T[] | undefined = [],
  createId: () => string = generateObjectId,
): Array<T & {questionId: string}> =>
  questions.map((question) => ({
    ...question,
    questionId: question.questionId || createId(),
  }));

export const getCheckinProgress = (
  values: CheckinFormValues | undefined,
  checkin?: ICurrentCheckinsInfoResponseData | null,
): CheckinProgress => {
  const questions = values?.questions?.questions || [];
  const requiredQuestions = questions.filter(
    (question) =>
      !isGoalsReflectionQuestion(question.question) &&
      isQuestionRequired(question),
  );
  const answeredQuestions = requiredQuestions.filter((question) =>
    hasAnswer(question.response),
  ).length;
  const pulseIsRequired =
    !!checkin?.params?.includes('pulse') &&
    checkin.responses?.pulse?.required !== false;
  const pulseIsAnswered = pulseIsRequired && !!values?.pulse?.mood;
  const total = requiredQuestions.length + (pulseIsRequired ? 1 : 0);
  const answered = answeredQuestions + (pulseIsAnswered ? 1 : 0);
  const canSubmit = total === 0 || answered === total;

  return {
    answered,
    total,
    percentage: total === 0 ? 100 : Math.round((answered / total) * 100),
    canSubmit,
  };
};

export const formatContentSummary = (
  checkin: ICurrentCheckinsInfoResponseData,
  cycle?: {content?: ICheckinCycleDetail['content']},
) => {
  const configuredQuestions = (cycle?.content?.questions ||
    checkin.responses?.questions?.questions ||
    []) as Array<{question?: string}>;
  const hasGoals = configuredQuestions.some((question) =>
    isGoalsReflectionQuestion(question?.question),
  );
  const questionCount = configuredQuestions.filter(
    (question) => !isGoalsReflectionQuestion(question?.question),
  ).length;
  const checklistCount =
    cycle?.content?.priorities?.length ||
    (checkin.params?.includes('priorities') ? 1 : 0);
  const pulseCount =
    cycle?.content?.pulse?.length ||
    (checkin.params?.includes('pulse') ? 1 : 0);
  const parts = [
    hasGoals ? 'Goal reflection' : '',
    questionCount
      ? `${questionCount} question${questionCount === 1 ? '' : 's'}`
      : '',
    CHECKLISTS_ENABLED && checklistCount
      ? `${checklistCount} checklist${checklistCount === 1 ? '' : 's'}`
      : '',
    pulseCount ? `${pulseCount} pulse${pulseCount === 1 ? '' : 's'}` : '',
  ].filter(Boolean);

  return parts.join(' · ');
};

export const getDueMeta = (
  checkin: CheckinDeadlineInput,
  now = dayjs(),
): CheckinDueMeta => {
  const {dueDate, isOverdue, daysOverdue} = getCheckinDeadline(checkin, now);
  const dueLabel = isOverdue
    ? daysOverdue
      ? `${daysOverdue} day${daysOverdue === 1 ? '' : 's'} overdue`
      : 'Overdue'
    : dueDate?.isSame(now, 'day')
    ? 'Due today'
    : dueDate?.isSame(now, 'week')
    ? `Due ${dueDate.format('dddd')}`
    : dueDate
    ? `Due ${dueDate.format('MMM D')}`
    : undefined;

  return {
    dueDate,
    dueLabel,
    dueDateLabel:
      isOverdue && dueDate ? `Due ${dueDate.format('MMM D')}` : undefined,
    isOverdue,
  };
};

export const formatSentLabel = (
  checkin: Pick<
    ICurrentCheckinsInfoResponseData,
    'createdAt' | 'weekOfYear' | 'year'
  >,
) => {
  if (checkin.createdAt)
    return `Sent ${dayjs(checkin.createdAt).format('MMM D')}`;
  if (checkin.weekOfYear && checkin.year) {
    return `Sent ${dayjs(
      getWeekRange(checkin.weekOfYear, checkin.year).starts,
    ).format('MMM D')}`;
  }
  return 'Sent date unavailable';
};

export const getCreatorName = (
  createdBy:
    | ICheckinCycleDetail['createdBy']
    | {id?: string; firstName?: string; lastName?: string; email?: string}
    | undefined,
  users: Array<{
    id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  }>,
) => {
  const creator =
    typeof createdBy === 'object'
      ? createdBy
      : users.find((user) => user.id === createdBy);
  if (!creator) return 'Cycle creator';
  const fullName = `${creator.firstName || ''} ${
    creator.lastName || ''
  }`.trim();
  return fullName || creator.email || 'Cycle creator';
};
